import React, { useEffect, useState, ReactNode } from 'react';

import { AlignJustify, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../../components/layout/Sidebar';
import Topbar from '../../components/layout/Topbar';
import { getRoles, getPermissions } from '../../utils/tokenUtils';

interface Breadcrumb {
  label: string;
  href: string;
}

interface AdminLayoutProps {
  activeMenuItem: string;
  breadcrumbs: Breadcrumb[];
  allowedRoles: string[];
  children: ReactNode;
  requiredPermission?: string;
  // A phone top bar (MobilePageBar) for the member's own app-like pages. When set, it replaces
  // Topbar and the breadcrumb bar below lg, so the page has no hamburger, no breadcrumbs and no
  // account menu on a phone. From lg up it is ignored and the desktop layout is unchanged.
  mobileBar?: ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  activeMenuItem,
  breadcrumbs,
  children,
  allowedRoles,
  requiredPermission,
  mobileBar,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roles, setRoles] = useState<string[] | null>(null);
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [previousPath, setPreviousPath] = useState<string>('/login');

  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== '/login') {
      setPreviousPath(location.pathname);
    }
  }, [location]);

  useEffect(() => {
    const userRoles = getRoles();
    const userPermissions = getPermissions();
    setRoles(userRoles);
    setPermissions(userPermissions);
  }, []);

  useEffect(() => {
    if (roles && permissions) {
      const hasRole = roles.some((role) => allowedRoles.includes(role));
      const hasPermission =
        !requiredPermission || permissions.includes(requiredPermission);

      if (!hasRole || !hasPermission) {
        // Try to go back in history first
        if (window.history.length > 2) {
          navigate(-1);
        } else {
          // If no history, try the stored previous path
          if (previousPath && previousPath !== location.pathname) {
            navigate(previousPath);
          } else {
            // If all else fails, go to dashboard
            navigate('/login');
          }
        }
      }
    }
  }, [
    roles,
    permissions,
    allowedRoles,
    requiredPermission,
    navigate,
    previousPath,
    location.pathname,
  ]);

  const toggleSidebar = (): void => {
    setSidebarOpen(!sidebarOpen);
  };

  // Below lg the page itself scrolls, not an inner box. iOS only scrolls to the top on a tap of the
  // status bar when the document is what scrolls, so an overflow-auto content box made that tap do
  // nothing on every member and admin page. The two bars stay pinned with sticky instead (Topbar
  // is h-20, the breadcrumb bar h-12: 8rem in all, which is what Form/Tab's sticky headers and the
  // mobile Sidebar offset by). From lg up it is the fixed-height layout with an inner scroll box,
  // unchanged, since a desktop has no status bar to tap. getMainScrollContainer() picks whichever
  // of the two is scrolling.
  return (
    <div className="flex min-h-screen flex-col bg-gray-50 lg:h-screen dark:bg-secondary">
      {/* Phone bar for the member's own pages. It replaces both bars below, so nothing else on
          those pages is offset by their 8rem (no Form/Tab sticky header, no hamburger Sidebar). */}
      {mobileBar && (
        <div className="sticky top-0 z-30 lg:hidden">{mobileBar}</div>
      )}

      {/* Topbar */}
      <div
        className={`sticky top-0 z-30 lg:static ${mobileBar ? 'hidden lg:block' : ''}`}
      >
        <Topbar />
      </div>

      <div className="flex flex-1 lg:overflow-hidden">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} activePage={activeMenuItem} />

        {/* Content Area */}
        <div className="flex min-w-0 flex-1 flex-col bg-gray-50 lg:overflow-hidden dark:bg-secondary">
          {/* Breadcrumb with white background. Opaque below lg: it is pinned over scrolling
              content there, and the dark theme's white/5 would let that content show through. */}
          <div
            className={`sticky top-20 z-20 h-12 items-center space-x-4 border-b border-gray-200 bg-white px-4 py-3 lg:static lg:flex dark:border-white/10 dark:bg-secondary lg:dark:bg-white/5 ${mobileBar ? 'hidden' : 'flex'}`}
          >
            {/* Icon Button */}
            <button
              className="rounded-lg p-3 text-ink-muted focus:outline-none lg:hidden dark:text-white/70"
              onClick={toggleSidebar}
            >
              <AlignJustify className="w-6 h-6" />
            </button>

            {/* Breadcrumb Navigation */}
            <nav className="font-display text-base font-semibold text-ink dark:text-white">
              <ol className="inline-flex space-x-1">
                {breadcrumbs.map((breadcrumb, index) => (
                  <li key={index} className="inline-flex items-center">
                    <a
                      href={breadcrumb.href}
                      className={`${
                        index === breadcrumbs.length - 1
                          ? 'text-ink-muted dark:text-white/50' // The current page, not a link
                          : 'text-ink hover:text-primary-deep hover:underline dark:text-white dark:hover:text-primary'
                      }`}
                    >
                      {breadcrumb.label}
                    </a>
                    {index < breadcrumbs.length - 1 && (
                      <ChevronRight className="mx-1 h-4 w-4 text-ink-muted dark:text-white/40" />
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>

          {/* overflow-x-auto below lg keeps a wide table scrolling sideways in here rather than
              widening the whole page. It has no height limit there, so it never scrolls up and
              down, and the document still does. */}
          <div
            className="flex-1 overflow-x-auto lg:overflow-auto"
            data-main-container
          >
            <div className="max-w-full">
              <main className="min-w-0">{children}</main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
